// import React, { useEffect, useState } from "react";
// import { useParams } from "react-router-dom";
// import Lightbox from "yet-another-react-lightbox";
// import "yet-another-react-lightbox/styles.css";
// import api from "../api/axiosInstance";

// const EventDetailsPage = () => {
//   const { id } = useParams();
//   const [event, setEvent] = useState(null);
//   const [loading, setLoading] = useState(true);

//   const [open, setOpen] = useState(false);
//   const [index, setIndex] = useState(0);

//   useEffect(() => {
//     const fetchEvent = async () => {
//       try {
//         const res = await api.get(`/events/get/by-id/${id}`);
//         setEvent(res.data);
//       } catch (err) {
//         console.error("Failed to fetch event:", err);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchEvent();
//   }, [id]);

//   if (loading) return <p>Loading...</p>;
//   if (!event) return <p className="text-center py-10">Event not found.</p>;

//   const images = event?.imageUrls || [];

//   // Format date nicely
//   const formatDate = (dateStr) => {
//     return new Date(dateStr).toLocaleDateString("en-US", {
//       year: "numeric",
//       month: "long",
//       day: "numeric",
//     });
//   };

//   return (
//     <section className="max-w-6xl mx-auto py-16 px-6">
//       {/* Title */}
//       <h1 className="text-4xl font-bold mb-2">{event.title}</h1>

//       {/* Dates */}
//       <p className="text-lg text-gray-600 mb-8">
//         {event.startDate === event.endDate
//           ? `Date: ${formatDate(event.startDate)}`
//           : `From ${formatDate(event.startDate)} to ${formatDate(
//               event.endDate
//             )}`}
//       </p>

//       {/* Images Grid */}
//       {images.length > 0 && (
//         <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
//           {images.map((img, idx) => (
//             <img
//               key={idx}
//               src={img}
//               alt={`${event.title} - ${idx + 1}`}
//               className="w-full h-[250px] object-cover rounded-xl shadow-md hover:scale-105 transition-transform cursor-pointer"
//               onClick={() => {
//                 setIndex(idx);
//                 setOpen(true);
//               }}
//             />
//           ))}
//         </div>
//       )}

//       {/* Lightbox Viewer */}
//       {images.length > 0 && (
//         <Lightbox
//           open={open}
//           close={() => setOpen(false)}
//           index={index}
//           slides={images.map((src) => ({ src }))}
//           carousel={{ finite: false }}
//         />
//       )}
//     </section>
//   );
// };

// export default EventDetailsPage;

import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  ArrowLeft,
  Share2,
  Download,
  ChevronRight,
  Trophy,
  Star,
  Image as ImageIcon,
  Maximize2,
} from "lucide-react";
import api from "../api/axiosInstance";

const EventDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const res = await api.get(`/events/get/by-id/${id}`);
        setEvent(res.data);
      } catch (err) {
        console.error("Failed to fetch event:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateTime = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isUpcoming = () => {
    if (!event?.startDate) return false;
    return new Date(event.startDate) > new Date();
  };

  const isOngoing = () => {
    if (!event?.startDate || !event?.endDate) return false;
    const now = new Date();
    const start = new Date(event.startDate);
    const end = new Date(event.endDate);
    return now >= start && now <= end;
  };

  const getEventStatus = () => {
    if (isOngoing()) {
      return {
        text: "Happening Now",
        color: "bg-gradient-to-r from-emerald-500 to-lime-500",
        icon: <Clock className="w-4 h-4" />,
      };
    }
    if (isUpcoming()) {
      return {
        text: "Upcoming",
        color: "bg-gradient-to-r from-blue-500 to-indigo-500",
        icon: <Calendar className="w-4 h-4" />,
      };
    }
    return {
      text: "Completed",
      color: "bg-gradient-to-r from-amber-500 to-orange-500",
      icon: <Trophy className="w-4 h-4" />,
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-lime-50">
        <div className="text-center">
          <div className="inline-flex items-center gap-3">
            <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-gray-600">Loading event details...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-lime-50">
        <div className="text-center max-w-md p-8 bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-emerald-100">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-50 to-lime-50 flex items-center justify-center mx-auto mb-4">
            <Star className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Event Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            The event you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate("/events")}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold bg-gradient-to-r from-emerald-500 to-lime-500 text-white hover:from-emerald-600 hover:to-lime-600 transition-all duration-300"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Events
          </button>
        </div>
      </div>
    );
  }

  const images = event?.imageUrls || [];
  const status = getEventStatus();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-lime-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-900 via-emerald-800 to-lime-800 text-white py-8">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors duration-300"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>

            <div
              className={`px-4 py-2 rounded-full text-white text-sm font-semibold flex items-center gap-2 ${status.color}`}
            >
              {status.icon}
              {status.text}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-12">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-8 md:p-10 border border-emerald-100">
          {/* Event Header */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-50 to-lime-50 flex items-center justify-center">
                <Trophy className="w-7 h-7 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">
                  {event.title}
                </h1>
                <p className="text-xl text-gray-600">
                  {event.subtitle || "RGCC Premium Event"}
                </p>
              </div>
            </div>

            {/* Event Metadata */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="flex items-center gap-3 p-4 bg-gradient-to-br from-emerald-50 to-lime-50 rounded-xl border border-emerald-100">
                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Date </div>
                  <div className="font-semibold text-gray-900">
                    {event.startDate === event.endDate
                      ? formatDate(event.startDate)
                      : `${formatDate(event.startDate)} - ${formatDate(
                          event.endDate
                        )}`}
                  </div>
                </div>
              </div>

              {event.location && (
                <div className="flex items-center gap-3 p-4 bg-gradient-to-br from-emerald-50 to-lime-50 rounded-xl border border-emerald-100">
                  <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Location</div>
                    <div className="font-semibold text-gray-900">
                      {event.location}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 p-4 bg-gradient-to-br from-emerald-50 to-lime-50 rounded-xl border border-emerald-100">
                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
                  <Users className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Participants</div>
                  <div className="font-semibold text-gray-900">
                    {event.participants || "Members & Guests"}
                  </div>
                </div>
              </div>
            </div>

            {/* Event Description */}
            {event.description && (
              <div className="mb-10">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  About This Event
                </h3>
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {event.description}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Images Gallery */}
          {images.length > 0 && (
            <div className="mb-10">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <ImageIcon className="w-6 h-6" />
                  Event Gallery
                </h3>
                <div className="text-gray-500">
                  {images.length} {images.length === 1 ? "Photo" : "Photos"}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {images.map((img, idx) => (
                  <div
                    key={idx}
                    className="relative group cursor-pointer rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500"
                    onClick={() => {
                      setIndex(idx);
                      setOpen(true);
                    }}
                  >
                    <img
                      src={img}
                      alt={`${event.title} - ${idx + 1}`}
                      className="w-full h-64 object-cover transform group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="flex items-center justify-between text-white">
                          <span className="font-medium">Photo {idx + 1}</span>
                          <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                            <Maximize2 className="w-4 h-4" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pt-8 border-t border-gray-100">
            {/* <div className="text-gray-600">
              <span className="font-medium">Event ID:</span>{" "}
              {event._id?.slice(-8) || id}
            </div> */}

            {/* <div className="flex items-center gap-4">
              <button className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold bg-gradient-to-r from-emerald-500 to-lime-500 text-white hover:from-emerald-600 hover:to-lime-600 transition-all duration-300 shadow-lg hover:shadow-xl">
                <Calendar className="w-4 h-4" />
                Register Now
              </button>

              <button className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all duration-300">
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div> */}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {images.length > 0 && (
        <Lightbox
          open={open}
          close={() => setOpen(false)}
          index={index}
          slides={images.map((src) => ({ src }))}
          carousel={{ finite: false }}
          controller={{ closeOnBackdropClick: true }}
        />
      )}
    </div>
  );
};

export default EventDetailsPage;
